import React, { useEffect, useState } from 'react';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  LoadingButton,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';

import { useForm, useNavigate, useSelector, useTranslation } from 'third-party';

import { authThirdParty } from 'constants/endpoints';
import {
  forgotPasswordUrl,
  tenantDashboardUrl,
  privacyPolicyUrl,
  registerUrl,
  termsAndConditionsUrl,
} from 'constants/url';

import { useApp } from 'modules/app';

import { appSelectors } from 'selectors';

import { FormField } from 'components/FormField/FormField';

import { emailField } from 'utils/validations';

const MicrosoftIcon = () => (
  <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, display: 'block' }}>
    <path d="M10 0h10v10H10z" fill="#f25022"/>
    <path d="M0 0h9v10H0z" fill="#7fba00"/>
    <path d="M0 11h9v10H0z" fill="#00a4ef"/>
    <path d="M10 11h10v10H10z" fill="#ffb900"/>
  </svg>
);

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, display: 'block' }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const RegisterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style={{ width: 20, height: 20, display: 'block' }}>
    <path fill="currentColor" d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

enum FormFields {
  Email = 'email',
  Password = 'password',
}

const loginFormDefaultValues = {
  [FormFields.Email]: '',
  [FormFields.Password]: '',
};

type LoginFormData = typeof loginFormDefaultValues;

export const Login = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<typeof loginFormDefaultValues>({
    defaultValues: loginFormDefaultValues,
  });

  const navigate = useNavigate();
  const { userLogIn, userInfo, selectTenant } = useApp();
  const { error } = useNotifications();
  const userInfoPending = useSelector(appSelectors.userInfoPending);
  const userLoginInPending = useSelector(appSelectors.userLoginInPending);
  const user = useSelector(appSelectors.user);

  useEffect(() => {
    if (!userInfoPending && user) {
      const firstTenant = user.userTenants?.[0];
      if (firstTenant) {
        selectTenant(firstTenant.tenant.id);
        navigate(tenantDashboardUrl(firstTenant.tenant.id));
      }
    }
  }, [navigate, user, userInfoPending]);

  const onSubmit = async ({ email, password }: LoginFormData) => {
    const result = await userLogIn(email, password);

    if (result.error) {
      error(t('login.invalidCredentials'));
      return;
    }
    const infoResult = await userInfo();
    const firstTenant = infoResult.payload?.userTenants?.[0];
    if (firstTenant) {
      selectTenant(firstTenant.tenant.id);
      navigate(tenantDashboardUrl(firstTenant.tenant.id));
    }
  };

  const onRegisterClick = () => {
    navigate(registerUrl());
  };

  const onForgotPasswordClick = () => {
    navigate(forgotPasswordUrl());
  };

  const onMicrosoftLoginClick = () => {
    window.location.href = authThirdParty('microsoft');
  };

  const onGoogleLoginClick = () => {
    window.location.href = authThirdParty('google');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '440px' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: '28px' }}>
        <Typography
          sx={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#32363F',
            mb: '8px',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          {t('login.header')}
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            color: '#6B7280',
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          {t('login.subHeader')}
        </Typography>
      </Box>

      {/* Form */}
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormField
          fieldName={FormFields.Email}
          fieldError={errors[FormFields.Email]}
          placeholder={t('common.form.email.placeholder')}
          requiredErrorMessage={t('errors.fields.emptyFieldError')}
          control={control}
          isDisabled={userLoginInPending}
          maxLength={emailField.maxLength}
          isRequired
          showIsRequiredMark={false}
          rules={{
            validate: {
              invalidEmail: (value: string) =>
                emailField.validate(value) ||
                t('common.form.email.error.invalid'),
            },
          }}
        />

        <FormField
          type={showPassword ? 'text' : 'password'}
          fieldName={FormFields.Password}
          fieldError={errors[FormFields.Password]}
          placeholder={t('common.form.password.placeholder')}
          requiredErrorMessage={t('errors.fields.emptyFieldError')}
          control={control}
          maxLength={emailField.maxLength}
          isDisabled={userLoginInPending}
          isRequired
          showIsRequiredMark={false}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  sx={{ padding: 0, color: '#6B7280', '&:hover': { color: '#E66A10' } }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Forgot password */}
        <Box display="flex" justifyContent="flex-end" sx={{ mt: '-8px' }}>
          <Link
            component="button"
            onClick={onForgotPasswordClick}
            type="button"
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#E66A10',
              textDecoration: 'none',
              fontFamily: 'Montserrat, sans-serif',
              '&:hover': { color: '#32363F', textDecoration: 'underline' },
            }}
          >
            {t('login.forgotPassword')}
          </Link>
        </Box>

        {/* Sign In */}
        <LoadingButton
          type="submit"
          variant="contained"
          size="medium"
          color="primary"
          loading={userLoginInPending}
          fullWidth
          sx={{ py: '9px', textTransform: 'uppercase' }}
        >
          {t('login.signIn')}
        </LoadingButton>

        {/* Social sign-in buttons */}
        <Button
          variant="outlined"
          size="medium"
          onClick={onMicrosoftLoginClick}
          disabled={userLoginInPending}
          startIcon={<MicrosoftIcon />}
          fullWidth
          sx={{
            borderColor: '#E5E7EB',
            color: '#32363F',
            borderWidth: '1px',
            textTransform: 'uppercase',
            fontSize: '13px',
            bgcolor: '#FFFFFF',
            '&:hover': {
              bgcolor: '#F9FAFB',
              borderColor: '#D1D5DB',
              borderWidth: '1px',
              color: '#32363F',
            },
          }}
        >
          Sign in with Microsoft
        </Button>

        <Button
          variant="outlined"
          size="medium"
          onClick={onGoogleLoginClick}
          disabled={userLoginInPending}
          startIcon={<GoogleIcon />}
          fullWidth
          sx={{
            borderColor: '#E5E7EB',
            color: '#32363F',
            borderWidth: '1px',
            textTransform: 'uppercase',
            fontSize: '13px',
            bgcolor: '#FFFFFF',
            '&:hover': {
              bgcolor: '#F9FAFB',
              borderColor: '#D1D5DB',
              borderWidth: '1px',
              color: '#32363F',
            },
          }}
        >
          Sign in with Google
        </Button>

        {/* Divider */}
        <Divider
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: 'Montserrat, sans-serif',
            '&::before, &::after': { borderColor: '#E5E7EB' },
          }}
        >
          {t('login.continueWith')}
        </Divider>

        {/* Register */}
        <Button
          variant="outlined"
          size="medium"
          color="primary"
          onClick={onRegisterClick}
          fullWidth
          startIcon={<RegisterIcon />}
          sx={{ textTransform: 'uppercase' }}
        >
          {t('login.register')}
        </Button>

        {/* Terms */}
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#6B7280',
            fontFamily: 'Montserrat, sans-serif',
            mt: '4px',
          }}
        >
          {t('login.terms')
            .split(/(Terms of Service|Privacy Policy)/)
            .map((text, index) => {
              if (text === 'Terms of Service') {
                return (
                  <Link
                    key={index}
                    component="button"
                    onClick={() => navigate(termsAndConditionsUrl())}
                    type="button"
                    sx={{
                      fontSize: '12px',
                      color: '#E66A10',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {text}
                  </Link>
                );
              } else if (text === 'Privacy Policy') {
                return (
                  <Link
                    key={index}
                    component="button"
                    onClick={() => navigate(privacyPolicyUrl())}
                    type="button"
                    sx={{
                      fontSize: '12px',
                      color: '#E66A10',
                      fontWeight: 500,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {text}
                  </Link>
                );
              }
              return text;
            })}
        </Typography>
      </Box>
    </Box>
  );
};
